/**
 * onboardingAnswer — the server's answer to "where is this account in
 * onboarding?", and the ONE fact every onboarding behaviour on the phone obeys:
 * the greeting and its pre-warmed opener, the step-1 opener (the name
 * question), the welcome tour, and the opener safety net.
 *
 * WHY (2026-09-20). Kirsten (044901ed, onboarded 2026-05-01, step 8) was asked
 * "What name would you like me to call you when we chat?". A chat screen came up
 * with no messages and no saved `backendState`, and every onboarding behaviour
 * read that silence as "new user": the opener safety net gates on
 * `!backendState?.onboardingCompleted`, and `useSubscription` starts at
 * `onboardingStep 0 / onboardingCompleted false` — defaults indistinguishable
 * from a real answer. The server knows where the account is; the phone asks it
 * (`/api/user/subscription-status`, fetched on every native mount by
 * useSubscription), keeps the answer FOR THE ACCOUNT IT ASKED ABOUT, and treats
 * "no answer yet" as unknown — never as new.
 *
 * PHASES
 *   unknown      no answer for this account yet. A default is not an answer; an
 *                answer left over from another account on the phone does not count.
 *   new          step 0, not completed
 *   mid          steps 1-6 (the scripted opener)
 *   established  step >= 7 (STEP_7_DISCOVERY) or completed
 *
 * Unknown means WAIT, not guess — the answer normally lands in 1-2 s, before a
 * greeting would render anyway. The wait is capped (ONBOARDING_ANSWER_WAIT_MS)
 * so a failed check can never strand a genuine new signup: past the cap callers
 * fall back to the local rule they used before this module, and say so.
 *
 * The server holds the same line: /api/chat refuses the onboarding sentinel for
 * an established account (services/onboarding_sentinel_guard.py, #3865).
 */

import { decodeJwtPayload } from '../utils/jwtExpiry';

export type OnboardingPhase = 'unknown' | 'new' | 'mid' | 'established';

/** allow = go ahead; forbid = never for this account; wait = no answer yet. */
export type OnboardingDecision = 'allow' | 'forbid' | 'wait';

export interface OnboardingAnswer {
  /** The account the server answered for (the credential's `sub`). */
  userId: string;
  step: number;
  completed: boolean;
}

/**
 * The first step past the scripted opener: OnboardingStep.STEP_7_DISCOVERY in
 * services/bulletproof_onboarding_engine.py, and REFUSED_FROM_STEP in
 * services/onboarding_sentinel_guard.py. Pinned to both by
 * tests/gate/test_onboarding_phase_boundary.py.
 */
export const ESTABLISHED_ONBOARDING_STEP = 7;

/** The longest a behaviour waits for the answer before falling back. */
export const ONBOARDING_ANSWER_WAIT_MS = 4000;

let current: OnboardingAnswer | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

export function recordOnboardingAnswer(answer: OnboardingAnswer): void {
  current = { userId: answer.userId, step: answer.step, completed: answer.completed };
  notify();
}

/** No credential / signed out: no account's answer is known any more. */
export function forgetOnboardingAnswer(): void {
  if (current === null) return;
  current = null;
  notify();
}

/** The answer for THIS account, or null. */
export function onboardingAnswerFor(userId: string | null | undefined): OnboardingAnswer | null {
  if (!userId || !current || current.userId !== userId) return null;
  return current;
}

export function phaseOf(answer: OnboardingAnswer | null): OnboardingPhase {
  if (!answer) return 'unknown';
  if (answer.completed || answer.step >= ESTABLISHED_ONBOARDING_STEP) return 'established';
  if (answer.step <= 0) return 'new';
  return 'mid';
}

export function subscribeOnboardingAnswer(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Resolves with the answer for `userId` as soon as it is known, or null once
 * `timeoutMs` passes without one (or at once when there is no account).
 */
export function waitForOnboardingAnswer(
  userId: string | null | undefined,
  timeoutMs: number = ONBOARDING_ANSWER_WAIT_MS,
): Promise<OnboardingAnswer | null> {
  if (!userId) return Promise.resolve(null);
  const known = onboardingAnswerFor(userId);
  if (known) return Promise.resolve(known);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (answer: OnboardingAnswer | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(answer);
    };
    const unsubscribe = subscribeOnboardingAnswer(() => {
      const answer = onboardingAnswerFor(userId);
      if (answer) finish(answer);
    });
    const timer = setTimeout(() => finish(null), timeoutMs);
  });
}

/** Greeting, pre-warmed opener, welcome tour: new accounts only. */
export function newUserBehaviour(phase: OnboardingPhase): OnboardingDecision {
  if (phase === 'unknown') return 'wait';
  return phase === 'new' ? 'allow' : 'forbid';
}

/**
 * The step-1 opener — the name question, and the `__START_ONBOARDING__` request
 * that fetches it: only while the account is at step 0 or 1 and not completed.
 * Past step 1 the question has been asked and answered.
 */
export function openerRequest(answer: OnboardingAnswer | null): OnboardingDecision {
  if (!answer) return 'wait';
  if (answer.completed) return 'forbid';
  return answer.step <= 1 ? 'allow' : 'forbid';
}

/**
 * The welcome tour's gate. With an answer: new accounts only. Without one yet:
 * wait — and once the wait is over (`waitedOut`), no veto from here: the tour's
 * own local checks decide, as they did before this module.
 */
export function tourGate(answer: OnboardingAnswer | null, waitedOut: boolean): OnboardingDecision {
  if (answer) return newUserBehaviour(phaseOf(answer));
  return waitedOut ? 'allow' : 'wait';
}

/**
 * The opener safety net's gate (ChatScreen BPA-07). With an answer: the step-1
 * opener rule. Without one yet: wait — and once the wait is over, the local
 * rule the net used before this module (`!backendState?.onboardingCompleted`).
 */
export function openerNetGate(
  answer: OnboardingAnswer | null,
  waitedOut: boolean,
  localInOnboarding: boolean,
): OnboardingDecision {
  if (answer) return openerRequest(answer);
  if (!waitedOut) return 'wait';
  return localInOnboarding ? 'allow' : 'forbid';
}

/**
 * The answer in a `/api/user/subscription-status` body, tagged with the account
 * the credential that fetched it belongs to. Null when either half is missing —
 * a body without the fields, or a credential with no subject, is not an answer.
 */
export function answerFromSubscriptionStatus(
  cedarJwt: string | null | undefined,
  body: unknown,
): OnboardingAnswer | null {
  if (!cedarJwt || !body || typeof body !== 'object') return null;
  const sub = decodeJwtPayload(cedarJwt)?.sub;
  if (typeof sub !== 'string' || !sub) return null;
  const { onboarding_step: step, onboarding_completed: completed } = body as {
    onboarding_step?: unknown;
    onboarding_completed?: unknown;
  };
  if (typeof step !== 'number' || !Number.isFinite(step) || typeof completed !== 'boolean') return null;
  return { userId: sub, step, completed };
}
